import './CompatibleDevicePopOver.scss';

import { DeviceCompatibility } from '@travelwin/cms/types';
import { Body, Icon, Title } from '@travelwin/core';
import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useExternalHomepageConfig } from '@/hooks/useExternalHomepageConfig';

import { AccordionList } from '../../AccordionList/AccordionList';
import Modal from '../../Modal';
import CompatibleDevicesInput from './CompatibleDevicesInput';

type CompatibleDevicePopOverProps = {
  open: boolean;
  handleClose: (open: boolean) => void;
  content: DeviceCompatibility | null | undefined;
};

const className = 'c-CompatibleDevicePopOver';

const CompatibleDevicePopOver: React.FC<CompatibleDevicePopOverProps> = ({
  open,
  handleClose,
  content,
}) => {
  const { t } = useTranslation();
  const { getHomepageUrl } = useExternalHomepageConfig();

  const [inputValue, setInputValue] = React.useState<string | null>(null);

  const compatibleDeviceModels = content?.map((item, index) => ({
    key: item?.id ?? `make-${index}`,
    label: item?.make,
    content: (
      <div className={`${className}__phone-model-container`}>
        {item?.models?.map((model) => (
          <div key={model.id} className={`${className}__phone-model`}>
            <span className={`${className}__phone-model-icon`}>
              <Icon name="checkCircleGreen" />{' '}
            </span>
            <Body size={16} weight={'800'} accent={'title'}>
              {model.model}
            </Body>
          </div>
        ))}
      </div>
    ),
  }));

  const displayCompatibleDevicesSearched = () => {
    const arrayOfModels =
      content?.flatMap((item) =>
        (item.models ?? []).map((model) => model.model),
      ) || [];

    const searchPhoneModels = (searchTerm: string) => {
      if (!searchTerm) {
        return [];
      }
      const lowerCaseSearchTerm = searchTerm.toLowerCase();

      return arrayOfModels.filter((model) => {
        return model.toLowerCase().includes(lowerCaseSearchTerm);
      });
    };

    const searchedPhones = searchPhoneModels(inputValue || '');
    if (searchedPhones.length === 0) {
      return;
    }
    return searchedPhones.map((model, index) => (
      <div key={index} className={`${className}__phone-model`}>
        <span className={`${className}__phone-model-icon`}>
          <Icon name="checkCircleGreen" />
        </span>
        <Body size={16} weight={'800'} accent={'title'}>
          {model}
        </Body>
      </div>
    ));
  };

  const onChange = (selectedValue: string) => {
    setInputValue(selectedValue);
  };

  const customHeader = () => {
    return (
      <>
        <Title level={3} tag={2} className={`${className}__title`}>
          {t('compatible_device_popup.compatible_device', 'Compatible devices')}
        </Title>
        <CompatibleDevicesInput
          placeholder={t(
            'compatible_device_popup.search_device',
            'Search for devices',
          )}
          onChange={onChange}
          value={inputValue || ''}
        />
        <div className={`${className}__full-list-container`}>
          <Body size={16} accent={'text'}>
            <Trans
              i18nKey="compatible_device_popup.full_list_of_esim_compatible_devices"
              defaults="or see the <a>full list</a> of eSIM-compatible devices."
              components={{
                a: <a href={getHomepageUrl('/support/compatible-handsets')} />,
              }}
            />
          </Body>
        </div>
      </>
    );
  };

  return (
    <Modal
      externalClassName={className}
      on={open}
      closeOnBackgroundClick={true}
      toggle={() => handleClose(false)}
      headerComponent={customHeader()}
    >
      {!inputValue && (
        <AccordionList items={compatibleDeviceModels ?? []} variant="text" />
      )}
      {displayCompatibleDevicesSearched()}
    </Modal>
  );
};

export default CompatibleDevicePopOver;
